import Button from '@/shared/Button'
import FormItem from '@/shared/FormCreator/FormItem'
import { yupResolver } from '@hookform/resolvers/yup'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

import debounce from '@mui/material/utils/debounce'
import unionBy from 'lodash/unionBy'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import * as Yup from 'yup'

import EditSmsTemplateLabels from '@/components/EditSmsTemplateLabels'
import SmsDialog from '@/components/SmsDialog'
import TextEditor from '@/shared/TextEditor'

const mapStateToProps = ({ loading }) => ({
  isLandingPageLoading: loading['landingpage/getAddableLandingPages'],
})

const inputConfig = {
  variant: 'standard',
  inputBaseProps: {
    labelSuffix: '',
    flexDirection: 'column',
    alignItems: 'stretch',
    spacing: 0,
  },
  endAdornment: args => {
    const { value, inputProps } = args

    const { maxLength } = inputProps || {}

    if (maxLength) {
      return (
        <Typography
          variant="caption"
          sx={{ color: '#00000040', fontSize: '14px !important' }}
        >{`${value.length}/${maxLength}`}</Typography>
      )
    }
    return null
  },
}

const placeholderOptions = [
  { text: 'Recipient full name', value: 'recipient_full_name' },
  { text: 'Recipient department', value: 'recipient_department' },
  { text: 'Recipient company', value: 'recipient_company' },
  { text: 'My first name', value: 'my_first_name' },
  { text: 'My last name', value: 'my_last_name' },
  { text: 'My email', value: 'my_email' },
  { text: 'My email domain', value: 'my_email_domain' },
  { text: 'Campaign start date', value: 'campaign_start_date' },
  { text: 'Campaign start date year', value: 'campaign_start_date_year' },
  { text: 'Campaign start date month', value: 'campaign_start_date_month' },
  { text: 'Campaign start date month fullname', value: 'campaign_start_date_month_fullname' },
  { text: 'Campaign start date day', value: 'campaign_start_date_day' },
  { text: 'Campaign start date time', value: 'campaign_start_date_time' },
  { text: 'Current Year', value: 'current_year' },
]

const SmsTemplateForm = ({
  dispatch,
  data,
  type,
  onSubmit: propsOnSubmit,
  isLandingPageLoading,
}) => {
  const { t } = useTranslation()

  const isSystem = type === 'system'

  const [preview, setPreview] = useState({})
  const [restoreContent, setRestoreContent] = useState('')
  const [showLabels, setShowLabels] = useState(false)
  const [selectedLabels, setSelectedLabels] = useState([])
  const [forceLandingPage, setForceLandingPage] = useState(false)

  const companyId = useSelector(state => state.auth.account?.companyId)

  Yup.addMethod(Yup.object, 'landingPageSelect', function testLandingPageSelect(errorMessage) {
    return this.test('test-landing-page-select', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { isLandingPage, landingPage } = parent

      if (isLandingPage && !landingPage) {
        return createError({ path, message: errorMessage })
      }

      return true
    })
  })

  const searchInputRef = useRef()
  const textEditorRef = useRef()

  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState({})
  const [page, setPage] = useState(0)

  const { data: searchResultsData = [], paging } = searchResults

  const fetchData = useCallback(() => {
    dispatch({
      type: 'landingPage/getAddableLandingPages',
      payload: {
        pageIndex: page,
        pageSize: 100,
        searchName: searchInputRef.current,
        type: isSystem ? 'S' : 'I',
      },
      successCallback: response => {
        const { data: responseData, paging: responsePaging } = response
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({ id: d.landingpageTempId, label: d.name }))

        setSearchResults(s => ({
          data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
          paging: responsePaging,
        }))
      },
    })
  }, [dispatch, isSystem, page])

  const loadMoreDebounce = debounce(() => {
    setPage(p => p + 1)
  }, 400)

  const fetchDataDebounce = useMemo(
    () =>
      debounce(() => {
        fetchData()
      }, 400),
    [fetchData],
  )

  useEffect(() => {
    fetchDataDebounce()
  }, [searchInput, page, fetchDataDebounce])

  let validations = {
    name: Yup.string().required(t('validation.template_name_required')),
    landingPage: Yup.object()
      .landingPageSelect(t('validation.landing_page_required'))
      .typeError(t('validation.landing_page_required')),
  }

  if (isSystem) {
    validations = {
      ...validations,
      difficulty: Yup.string().required(t('validation.difficulty_required')),
    }
  }

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    getValues,
    // eslint-disable-next-line no-unused-vars
    formState: { errors },
    reset: formReset,
  } = useForm({
    resolver: yupResolver(Yup.object().shape(validations)),
  })

  useEffect(() => {
    if (data) {
      const {
        name: formName,
        senderPhone: formSenderPhone,
        content: formContent,
        difficulty,
        smsTempLabelList: formLabelList,
        refLandingpage: formLandingPage,
        refLandingpageName,
      } = data

      if (difficulty !== null) {
        setValue('difficulty', difficulty)
      }

      if (formLandingPage && refLandingpageName) {
        setValue('isLandingPage', true)
        setValue('landingPage', { id: formLandingPage, label: refLandingpageName })
      }

      if (formName !== null) {
        setValue('name', formName)
      }

      if (formSenderPhone !== null) {
        setValue('senderPhone', formSenderPhone)
      }

      if (formContent !== null) {
        setRestoreContent(formContent)
      }

      const hasLandingPage = formContent?.includes('{{landing_page_url}}')
      setForceLandingPage(hasLandingPage)

      if (formLabelList !== null) {
        setSelectedLabels(
          formLabelList?.map(({ labelId, labelName }) => ({ id: labelId, label: labelName })) || [],
        )
      }
    }
  }, [data, setValue])

  useEffect(() => {
    if (selectedLabels) {
      setValue('labels', selectedLabels.map(({ label }) => label).join(','))
    }
    trigger?.('labels')
  }, [setValue, selectedLabels, trigger])

  const onSubmit = values => {
    const { name, difficulty, senderPhone, isLandingPage: isRefLandingPage, landingPage } = values

    const editorContent = textEditorRef.current.editor().getContent()

    // 將 &nbsp; 替換為空格
    const contentWithoutNbsp = editorContent.replaceAll('&nbsp;', ' ').replaceAll('<br />', '\n')

    const hasLandingPage = contentWithoutNbsp.includes('{{landing_page_url}}')

    const linkMissing = isRefLandingPage && !hasLandingPage

    if (!linkMissing) {
      const payload = {
        name,
        labelList: selectedLabels.map(({ label }) => label),
        senderPhone,
        isRefLandingPage,
        content: contentWithoutNbsp, // 使用已替換 &nbsp; 的內容
        type: isSystem ? 'SYSTEM' : 'INDIVIDUAL',
        ...(isEmpty(difficulty) ? {} : { difficulty }),
      }

      if (isRefLandingPage) {
        payload.refLandingpage = landingPage.id
      } else {
        payload.refLandingpage = null
      }

      propsOnSubmit?.(payload)
    }
    formReset?.({}, { keepValues: true })
  }

  const isLandingPage = watch('isLandingPage')
  const landingPage = watch('landingPage')

  useEffect(() => {
    if (isLandingPage === false) {
      setValue('landingPage', { id: '', label: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandingPage])

  return (
    <Container maxWidth={false}>
      <Card>
        <CardContent>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit, () => {
              trigger?.()
            })}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.template_name')}
                  defaultValue=""
                  name="name"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 80 } }}
                  errors={errors}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.label')}
                  defaultValue=""
                  name="labels"
                  control={control}
                  inputConfig={{
                    ...inputConfig,
                    InputProps: { readOnly: true },
                    onClick: () => {
                      setShowLabels(true)
                    },
                  }}
                  errors={errors}
                />
              </Grid>

              {isSystem && (
                <>
                  <Grid item xs={6}>
                    <FormItem
                      label={t('general.difficulty')}
                      defaultValue=""
                      name="difficulty"
                      type="select"
                      control={control}
                      inputConfig={{
                        ...inputConfig,
                        options: [
                          { value: 'EASY', label: t('general.easy') },
                          { value: 'MEDIUM', label: t('general.normal') },
                          { value: 'HARD', label: t('general.hard') },
                        ],
                      }}
                      errors={errors}
                    />
                  </Grid>

                  <Grid item xs={6} />
                </>
              )}

              <Grid item xs={6}>
                <FormItem
                  label={t('general.sender_phone')}
                  defaultValue=""
                  name="senderPhone"
                  type="select"
                  control={control}
                  inputConfig={{
                    ...inputConfig,
                    options: [{ value: '0935123456', label: '0935123456' }],
                  }}
                  errors={errors}
                />
              </Grid>

              <Grid item xs={6} />

              <Grid item xs={12}>
                <TextEditor
                  isSms={true}
                  ref={textEditorRef}
                  onInit={editor => {
                    setTimeout(() => {
                      if (data?.content) {
                        const temp = restoreContent.replaceAll(' ', '&nbsp;')
                        const formattedContent = temp.replaceAll('\n', '<br />')
                        editor.setContent(formattedContent)
                      }
                    }, 0)
                  }}
                  onContentChange={content => {
                    const hasLandingPage = content?.includes('{{landing_page_url}}')
                    setForceLandingPage(hasLandingPage)

                    if (hasLandingPage) {
                      setValue('isLandingPage', true)
                    } else {
                      setValue('isLandingPage', false)
                    }
                  }}
                  restoreContent={restoreContent}
                  placeholderOptions={placeholderOptions}
                  maxLength={200}
                />
              </Grid>

              <Grid container item xs={12}>
                <Grid item>
                  <FormItem
                    label={t('general.select_landing_page')}
                    defaultValue={false}
                    name="isLandingPage"
                    disabled={forceLandingPage}
                    type="checkbox"
                    control={control}
                    inputConfig={{
                      ...inputConfig,
                    }}
                    errors={errors}
                  />
                </Grid>

                {isLandingPage && (
                  <Grid item>
                    <Stack direction="row" spacing={2}>
                      <FormItem
                        name="landingPage"
                        type="autocomplete"
                        control={control}
                        errors={errors}
                        inputConfig={{
                          ...inputConfig,
                          sx: { width: 200, mt: 1 },
                          onInputChange: (_, value, reason) => {
                            searchInputRef.current = value

                            if (['input', 'clear'].includes(reason)) {
                              setPage(0)
                              setSearchInput(value)
                            }
                          },
                          onFocus: () => {
                            fetchDataDebounce()
                          },
                          onLoadMore: () => {
                            const { hasNext } = paging

                            if (!isLandingPageLoading && hasNext) {
                              loadMoreDebounce()
                            }
                          },
                          options: searchResultsData,
                          filterOptions: opts => opts,
                          isOptionEqualToValue: (option, value) => option.id === value.id,
                          renderOption: (renderProps, option) => (
                            <Box component="li" {...renderProps} key={option.id}>
                              {option.label}
                            </Box>
                          ),
                        }}
                      />

                      <Button
                        disabled={!landingPage}
                        onClick={() => {
                          dispatch({
                            type: 'landingPage/getLandingPageHtml',
                            payload: { landingpageTempId: landingPage.id },
                            successCallback: html => {
                              const w = window.open('', '_blank', '') // access the "about:blank" window you've opened
                              w.document.body.innerHTML = html
                            },
                          })
                        }}
                      >
                        {t('general.preview')}
                      </Button>
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <Button
                  onClick={() => {
                    const { senderPhone, name } = getValues() || {}
                    dispatch({
                      type: 'common/generatePreviewHtml',
                      payload: {
                        content: textEditorRef.current.editor().getContent(),
                        landingpageId: landingPage?.id,
                        type: 'SMS',
                      },
                      successCallback: ({ content }) => {
                        const parser = new DOMParser()
                        const doc = parser.parseFromString(content, 'text/html')

                        const anchors = doc.getElementsByTagName('a')

                        for (let i = 0; i < anchors.length; i += 1) {
                          anchors[i].setAttribute('target', '_blank')
                        }

                        setPreview({
                          details: {
                            smsContent: doc.documentElement.innerHTML,
                            senderPhone,
                          },
                          title: name,
                        })
                      },
                    })
                  }}
                >
                  {t('general.preview')}
                </Button>
              </Grid>

              {(companyId === 1 || !isSystem) && (
                <Grid item>
                  <Button type="submit">{t('general.complete')}</Button>
                </Grid>
              )}
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <SmsDialog
        open={!!preview.details}
        details={preview.details}
        title={preview.title}
        onClose={() => setPreview({})}
      />

      {showLabels && (
        <EditSmsTemplateLabels
          isEdit={!!data}
          smsTempId={data?.smsTempId} // TODO: uncertain
          open={showLabels}
          data={selectedLabels}
          onClose={() => {
            setShowLabels(false)
          }}
          onComplete={setSelectedLabels}
          type={type}
        />
      )}
    </Container>
  )
}

export default connect(mapStateToProps)(SmsTemplateForm)
